import { MessageContext } from '../context/MessageContext';
import imageCompression from 'browser-image-compression';
import { useEffect, useContext, useState } from 'react';
import axios from 'axios';

const FileUpload = ({ toLoadFiles, idCarOffer, newFiles, setNewFiles, existingFiles, setExistingFiles, setFilesToDelete }) => {
  const { setMess } = useContext(MessageContext);

  const [compressionProgress, setCompressionProgress] = useState({}); // Состояние прогресса сжатия для каждого видео
  const [ffmpeg, setFfmpeg] = useState(null); // Экземпляр FFmpeg для повторного использования


  // Загрузка существующих фото при изменении toLoadFiles
  useEffect(() => {
    if (!toLoadFiles) return;

    const loadExistingFiles = async (idCarOffer) => {
      try {
        const res = await axios.get(`/api/car-offer/photos/${idCarOffer}`);
        if (res.data.success) {
          setExistingFiles(res.data.photos);
        }
      } catch (err) {
        console.error('Ошибка загрузки фото:', err);
        setExistingFiles([]);
      }
    };

    loadExistingFiles(idCarOffer);
  }, [toLoadFiles]);

  // Обработка выбора файлов
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.type.startsWith('image/') || f.type.startsWith('video/'));

    if (validFiles.length !== files.length) {
      setMess({ color: 'red', title: 'Ошибка', desc: 'Некоторые файлы не являются изображениями или видео' });
      return;
    }

    // Проверка на превышение лимита
    const totalFiles = existingFiles.length + newFiles.length + validFiles.length;
    if (totalFiles > process.env.REACT_APP_MAX_UPLOAD_FILES) {
      setMess({ color: 'red', title: 'Ошибка', desc: 'Максимальное количество файлов - 10' });
    }

    // Создаём массив промисов для обработки каждого файла
    const processedFiles = await Promise.all(
      validFiles.map(async (file) => {
        if (file.type.startsWith('image/')) {
          // Обработка изображений — как было
          if (file.size > 2 * 1024 * 1024) { // > 2 MB
            try {
              const options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                fileType: 'image/jpeg'
              };
              const compressedFile = await imageCompression(file, options);
              return { file: compressedFile, type: 'image' };
            } catch (error) {
              console.error('Ошибка сжатия изображения:', error);
              setMess({ color: 'red', title: 'Ошибка', desc: 'Не удалось сжать изображение: ' + file.name });
              return null;
            }
          }
          return { file, type: 'image' };

        } else if (file.type.startsWith('video/')) {
          return { file, type: 'video', compressed: false };
        }
        return null;
      })
    );

    // Фильтруем успешные файлы
    const filteredProcessedFiles = processedFiles.filter(file => file !== null);

    // Формируем структуру данных для отображения
    const newFileList = filteredProcessedFiles.map(file => ({
      id: null,
      file: file.file,
      url: URL.createObjectURL(file.file),
      thumbnailUrl: URL.createObjectURL(file.file),
      isNew: true,
      isVideo: file.type === 'video',
      wasCompressed: file.type === 'video' && file.compressed // Отмечаем, было ли сжато
    }));

    setNewFiles(prev => [...prev, ...newFileList]);
    e.target.value = '';
  };

  // Удаление файла
  const handleFileDelete = (fileId, isExisting = false) => {
    if (isExisting) {
      setExistingFiles(prev => prev.filter(p => p.id !== fileId));
      // Добавляем в список для удаления на сервере
      const fileToDelete = existingFiles.find(p => p.id === fileId);
      if (fileToDelete) {
        setFilesToDelete(prev => [...prev, fileToDelete.id]);
      }
    } else {
      // Удаляем из newFiles
      setNewFiles(prev => prev.filter(p => p.url !== fileId));

      // Освобождаем URL объект
      const fileToRemove = newFiles.find(p => p.url === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.url);
        if (fileToRemove.thumbnailUrl !== fileToRemove.url) {
          URL.revokeObjectURL(fileToRemove.thumbnailUrl);
        }
      }
    }
  };

  // Форматирование размера файла
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Триггер выбора файла
  const triggerFileInput = () => {
    document.getElementById('file-input')?.click();
  };

  // Проверка на превышение лимита
  const totalFiles = existingFiles.length + newFiles.length;
  const isLimitExceeded = totalFiles > process.env.REACT_APP_MAX_UPLOAD_FILES;

  return (
    <div className="mt-4">
      {/* Кнопка выбора */}
      <button
        type="button"
        onClick={triggerFileInput}
        className={`w-full py-3 px-4 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors duration-200 text-gray-700 dark:text-gray-300
          ${isLimitExceeded
            ? 'border-red-500 bg-red-50 dark:bg-red-900/30 hover:border-red-600 dark:hover:border-red-700'
            : 'border-gray-300 dark:border-gray-600 hover:border-green-500 dark:hover:border-green-700'
          }`}
      >
        {isLimitExceeded
          ? `⚠️ Достигнут лимит (${process.env.REACT_APP_MAX_UPLOAD_FILES} файлов)`
          : newFiles.length > 0
            ? `Выбрано: ${newFiles.length} файлов`
            : `Выбрать фото или видео (max ${process.env.REACT_APP_MAX_UPLOAD_FILES} файлов)`}
      </button>
      <input
        id="file-input"
        type="file"
        multiple
        accept="image/*,video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Индикатор прогресса сжатия */}
      {/* {Object.keys(compressionProgress).length > 0 && (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-800">
          <h5 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">Сжатие видео...</h5>
          {Object.entries(compressionProgress).map(([key, progress]) => (
            <div key={key} className="mb-3">
              <div className="flex justify-between text-xs text-blue-700 dark:text-blue-400 mb-1">
                <span>Файл: {key.replace('compressed_', '')}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      )} */}

      {/* Существующие фото и видео */}
      {existingFiles.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Существующие фото и видео</h4>
          <div className="flex flex-wrap gap-3">
            {existingFiles.map(file => (
              <div key={file.id} className="relative inline-block">
                {file.mimetype?.startsWith('image/') ? (
                  <img
                    src={file.thumbnail_path}
                    alt="existing"
                    className="w-20 h-20 object-cover rounded-md"
                  />
                ) : (
                  <video
                    src={file.original_path}
                    className="w-20 h-20 object-cover rounded-md"
                    muted
                  />
                )}
                <button
                  type="button"
                  onClick={() => handleFileDelete(file.id, true)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Новые фото и видео */}
      {newFiles.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Новые фото и видео</h4>
          <div className="flex flex-wrap gap-3">
            {newFiles.map(file => (
              <div key={file.url} className="relative inline-block">
                {file.isVideo ? (
                  <div className="w-20 h-20 bg-gray-800 rounded-md flex items-center justify-center relative">
                    <video
                      src={file.thumbnailUrl}
                      className="w-full h-full object-cover"
                      muted
                    />
                    {file.wasCompressed && (
                      <span className="absolute -bottom-5 -right-2 bg-green-500 text-white text-xs px-1 rounded-full">Сжато</span>
                    )}
                  </div>
                ) : (
                  <img
                    src={file.thumbnailUrl}
                    alt="new"
                    className="w-20 h-20 object-cover rounded-md"
                  />
                )}
                <span className="absolute bottom-0 text-xs text-gray-500 dark:text-gray-400">
                  {formatFileSize(file.file.size)}
                </span>
                <button
                  type="button"
                  onClick={() => handleFileDelete(file.url, false)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs shadow-md hover:bg-red-600"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {newFiles.length === 0 && existingFiles.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Фото и видео не добавлены</p>
      )}
    </div>
  );
};

export default FileUpload;